// Plan 24a, Task 2.3 — Settings screen (Screen 3)
//
// Centered Panel (600px wide) with active settings only.
//
// Design system references:
//   - docs/design/tokens.json — color, spacing, typography, border-radius values
//   - docs/design/components.md §7 (Tab Bar), §21 (Settings Toggle)
//   - docs/design/screens.md — Settings (Screen 3)

import { createPanel } from '../components/panel';
import { createTabBar } from '../components/tab-bar';
import { createSlider } from '../components/slider';
import { createSettingsToggle } from '../components/settings-toggle';
import { createButton } from '../components/button';
import type { SliderHandle } from '../components/slider';
import type { SettingsToggleHandle } from '../components/settings-toggle';
import type { PanelHandle } from '../components/panel';
import type { TabBarHandle } from '../components/tab-bar';
import type { UIComponentHandle } from '../components/types';
import type { ScreenBackTarget, ScreenHandle, ScreenMountContext } from './types';
import { createAudioSettingsController, type AudioSettingsController } from '../../audio-settings';
import { areTutorialsHidden, setTutorialsHidden } from '../../portal-entry';

export type SettingsPayload = {
  readonly backTarget?: ScreenBackTarget;
  readonly audioSettings?: AudioSettingsController;
  readonly onBack?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────────────────────────────────────

function loadFromStorage(group: string, key: string, fallback: string): string {
  return localStorage.getItem(`smashparty_settings.${group}.${key}`) ?? fallback;
}

function saveToStorage(group: string, key: string, value: string | number): void {
  localStorage.setItem(`smashparty_settings.${group}.${key}`, String(value));
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab pane visibility helper
// ─────────────────────────────────────────────────────────────────────────────

function showOnlyPane(panes: HTMLElement[], activeIndex: number): void {
  panes.forEach((pane, i) => {
    pane.style.display = i === activeIndex ? 'block' : 'none';
  });
}

function toPercent(value: number): number {
  return Math.round(Math.min(1, Math.max(0, value)) * 100);
}

// ─────────────────────────────────────────────────────────────────────────────
// Label + slider row builder
// ─────────────────────────────────────────────────────────────────────────────

function createLabeledSlider(opts: {
  label: string;
  gsapRegistry: ScreenMountContext['gsapRegistry'];
  min: number;
  max: number;
  step: number;
  value: number;
  onInput: (v: number) => void;
}): { container: HTMLElement; sliderHandle: SliderHandle } {
  const { label, gsapRegistry, min, max, step, value, onInput } = opts;

  const container = document.createElement('div');
  container.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'gap: var(--spacing-xs, 4px)',
    'margin-bottom: var(--spacing-md, 16px)',
  ].join('; ');

  const labelEl = document.createElement('label');
  labelEl.textContent = label;
  labelEl.style.cssText = [
    "font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)",
    'font-size: var(--typography-size-body, 16px)',
    'color: var(--color-text-secondary, #B0B8C8)',
    'user-select: none',
  ].join('; ');
  container.appendChild(labelEl);

  const sliderHandle = createSlider({
    gsapRegistry,
    mount: container,
    min,
    max,
    step,
    value,
    onInput,
  });

  return { container, sliderHandle };
}

// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createSettingsScreen(
  ctx: ScreenMountContext<SettingsPayload | undefined>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;

  let disposed = false;
  const audioSettings =
    payload?.audioSettings ?? createAudioSettingsController({ getAudioManager: () => null });
  const initialAudioSettings = audioSettings.getSnapshot();

  // All component handles collected for dispose
  let panelHandle: PanelHandle | null = null;
  let tabBarHandle: TabBarHandle | null = null;
  let backButtonHandle: UIComponentHandle | null = null;
  const sliderHandles: SliderHandle[] = [];
  const toggleHandles: SettingsToggleHandle[] = [];

  // ── Root element (full-screen centered wrapper) ───────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-settings';
  root.style.cssText = [
    'position: fixed',
    'top: 0',
    'left: 0',
    'width: 100%',
    'height: 100%',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'z-index: 930',
    'box-sizing: border-box',
    'padding: max(12px, var(--app-safe-area-top)) max(12px, var(--app-safe-area-right)) max(12px, var(--app-safe-area-bottom)) max(12px, var(--app-safe-area-left))',
  ].join('; ');

  // ── Panel (600px wide, padding:lg) ───────────────────────────────────────

  panelHandle = createPanel({
    gsapRegistry,
    mount: root,
    padding: 'lg',
  });

  // Constrain panel width to 600px
  panelHandle.element.style.width = 'min(600px, calc(var(--app-viewport-width, 100vw) - 24px))';
  panelHandle.element.style.maxWidth = '100%';
  panelHandle.element.style.maxHeight = 'calc(var(--app-viewport-height, 100vh) - 24px)';
  panelHandle.element.style.overflowY = 'auto';
  panelHandle.element.style.overscrollBehavior = 'contain';
  panelHandle.element.style.boxSizing = 'border-box';

  // ── Build panel content ───────────────────────────────────────────────────

  const contentNode = document.createElement('div');
  contentNode.className = 'settings-panel-content';
  contentNode.style.cssText = [
    'display: flex',
    'flex-direction: column',
    'width: 100%',
    'box-sizing: border-box',
  ].join('; ');

  function navigateBack(): void {
    if (payload?.onBack != null) {
      payload.onBack();
      return;
    }
    const backTarget = payload?.backTarget;
    if (backTarget != null) {
      backTarget.beforeNavigate?.();
      router.navigate(backTarget.screenId, backTarget.payload);
      return;
    }
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  // Header row
  const headerRow = document.createElement('div');
  headerRow.className = 'settings-header';
  headerRow.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: space-between',
    'gap: var(--spacing-md, 16px)',
    'margin: 0 0 var(--spacing-md, 16px) 0',
  ].join('; ');

  backButtonHandle = createButton({
    gsapRegistry,
    variant: 'ghost',
    size: 'small',
    label: 'BACK',
    onClick: () => {
      if (!disposed) navigateBack();
    },
  });
  backButtonHandle.element.setAttribute(
    'aria-label',
    payload?.backTarget == null ? 'Back to main menu' : 'Back',
  );
  headerRow.appendChild(backButtonHandle.element);

  // Title
  const title = document.createElement('h2');
  title.textContent = 'SETTINGS';
  title.style.cssText = [
    "font-family: var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)",
    'font-size: var(--typography-size-h3, 28px)',
    'font-weight: var(--typography-weight-bold, 700)',
    'color: var(--color-text-primary, #FFFFFF)',
    'text-align: center',
    'text-transform: uppercase',
    'letter-spacing: var(--typography-letter-spacing-headline, 1px)',
    'margin: 0',
    'flex: 1',
  ].join('; ');
  headerRow.appendChild(title);

  const headerSpacer = document.createElement('div');
  headerSpacer.style.cssText = [
    'width: var(--layout-button-min-height, 48px)',
    'visibility: hidden',
    'pointer-events: none',
  ].join('; ');
  headerSpacer.setAttribute('aria-hidden', 'true');
  headerRow.appendChild(headerSpacer);

  contentNode.appendChild(headerRow);

  // ── Tab Bar ───────────────────────────────────────────────────────────────

  const TABS = [
    { label: 'AUDIO', id: 'audio' },
    { label: 'GRAPHICS', id: 'graphics' },
  ];

  // ── Tab panes ─────────────────────────────────────────────────────────────

  const tabBody = document.createElement('div');
  tabBody.className = 'settings-tab-body';
  tabBody.style.cssText = [
    'padding-top: var(--spacing-md, 16px)',
    'width: 100%',
    'box-sizing: border-box',
  ].join('; ');

  // ── AUDIO pane ────────────────────────────────────────────────────────────

  const audioPane = document.createElement('div');
  audioPane.className = 'settings-tab-pane';
  audioPane.dataset.tab = 'audio';
  audioPane.style.display = 'block';

  const { container: masterContainer, sliderHandle: masterSlider } = createLabeledSlider({
    label: 'Master Volume',
    gsapRegistry,
    min: 0,
    max: 100,
    step: 1,
    value: toPercent(initialAudioSettings.masterVolume),
    onInput: (v) => audioSettings.setSnapshot({ masterVolume: v / 100 }),
  });
  audioPane.appendChild(masterContainer);
  sliderHandles.push(masterSlider);

  const { container: musicContainer, sliderHandle: musicSlider } = createLabeledSlider({
    label: 'Music Volume',
    gsapRegistry,
    min: 0,
    max: 100,
    step: 1,
    value: toPercent(initialAudioSettings.musicVolume),
    onInput: (v) => audioSettings.setSnapshot({ musicVolume: v / 100 }),
  });
  audioPane.appendChild(musicContainer);
  sliderHandles.push(musicSlider);

  const { container: sfxContainer, sliderHandle: sfxSlider } = createLabeledSlider({
    label: 'SFX Volume',
    gsapRegistry,
    min: 0,
    max: 100,
    step: 1,
    value: toPercent(initialAudioSettings.sfxVolume),
    onInput: (v) => audioSettings.setSnapshot({ sfxVolume: v / 100 }),
  });
  audioPane.appendChild(sfxContainer);
  sliderHandles.push(sfxSlider);

  const musicMutedToggle = createSettingsToggle({
    gsapRegistry,
    label: 'Mute Music',
    value: initialAudioSettings.musicMuted,
    onChange: (v) => audioSettings.setSnapshot({ musicMuted: v }),
  });
  audioPane.appendChild(musicMutedToggle.element);
  toggleHandles.push(musicMutedToggle);

  const sfxMutedToggle = createSettingsToggle({
    gsapRegistry,
    label: 'Mute SFX',
    value: initialAudioSettings.sfxMuted,
    onChange: (v) => audioSettings.setSnapshot({ sfxMuted: v }),
  });
  audioPane.appendChild(sfxMutedToggle.element);
  toggleHandles.push(sfxMutedToggle);

  tabBody.appendChild(audioPane);

  // ── GRAPHICS pane ─────────────────────────────────────────────────────────

  const graphicsPane = document.createElement('div');
  graphicsPane.className = 'settings-tab-pane';
  graphicsPane.dataset.tab = 'graphics';
  graphicsPane.style.display = 'none';

  // Blood Effects toggle
  const bloodEffectsInitial = loadFromStorage('graphics', 'blood-effects', 'true') !== 'false';
  const bloodEffectsToggle = createSettingsToggle({
    gsapRegistry,
    label: 'Blood Effects',
    value: bloodEffectsInitial,
    onChange: (v) => saveToStorage('graphics', 'blood-effects', String(v)),
  });
  graphicsPane.appendChild(bloodEffectsToggle.element);
  toggleHandles.push(bloodEffectsToggle);

  const showTutorialsToggle = createSettingsToggle({
    gsapRegistry,
    label: 'Show Tutorials',
    value: !areTutorialsHidden(),
    onChange: (v) => setTutorialsHidden(!v),
  });
  graphicsPane.appendChild(showTutorialsToggle.element);
  toggleHandles.push(showTutorialsToggle);

  tabBody.appendChild(graphicsPane);

  // ── Assemble content node ─────────────────────────────────────────────────

  // Ordered tab panes
  const panes: HTMLElement[] = [audioPane, graphicsPane];

  // Tab bar created after panes so onChange can reference them
  tabBarHandle = createTabBar({
    gsapRegistry,
    tabs: TABS,
    activeIndex: 0,
    onChange: (index) => {
      showOnlyPane(panes, index);
    },
  });
  contentNode.appendChild(tabBarHandle.element);
  contentNode.appendChild(tabBody);

  // ── Wire panel ────────────────────────────────────────────────────────────

  panelHandle.setContent(contentNode);
  panelHandle.show();

  // ── Mount root into host ──────────────────────────────────────────────────

  host.appendChild(root);

  // ── ESC keydown handler ───────────────────────────────────────────────────

  const handleKeydown = (event: Event) => {
    if (disposed) return;
    if ((event as KeyboardEvent).key === 'Escape') {
      navigateBack();
    }
  };
  document.addEventListener('keydown', handleKeydown);

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Remove ESC listener
    document.removeEventListener('keydown', handleKeydown);

    // Dispose all component handles
    panelHandle?.dispose();
    tabBarHandle?.dispose();
    backButtonHandle?.dispose();
    for (const slider of sliderHandles) {
      slider.dispose();
    }
    for (const toggle of toggleHandles) {
      toggle.dispose();
    }
    // Remove root from host
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
